/**
 * Deleting a draft agreement, or withdrawing one that is out for signature (product owner
 * decision, 26 September 2026).
 *
 * One endpoint, `DELETE /agreements/{id}/` with a reason, and two different acts, so the
 * dialog says which one the reader is about to do:
 *   - a DRAFT reached nobody. It disappears from every list; a record of it and the
 *     reason stay.
 *   - an ISSUED letter may be open in somebody's inbox. It is withdrawn: the signing link
 *     stops working and the client sees it as "Withdrawn". A joint signatory who already
 *     signed stays on the record.
 * A signed letter is never offered this, and the server refuses it regardless.
 */

import { STAFF, qk, useApi, useApiMutation, type AgreementStatus } from '@legalcrm/api';
import { Form, FormActions, FormTextarea, Modal, useToast, useZodForm } from '@legalcrm/ui';
import { z } from 'zod';

const schema = z.object({
  reason: z.string().trim().min(3, 'Say why. It stays on the record permanently.'),
});

type Values = z.infer<typeof schema>;

/** Statuses the server will delete or withdraw from. Mirrors `agreements.services.WITHDRAWABLE`. */
export function canWithdraw(status: AgreementStatus): boolean {
  return status === 'draft' || status === 'sent' || status === 'viewed';
}

export interface WithdrawAgreementDialogProps {
  agreement: { id: string; title: string; status: AgreementStatus };
  onClose: () => void;
  /** Called after the server has accepted it. A deleted draft no longer exists to show. */
  onDone?: () => void;
}

export function WithdrawAgreementDialog({ agreement, onClose, onDone }: WithdrawAgreementDialogProps) {
  const api = useApi();
  const { toast } = useToast();
  const form = useZodForm(schema, { defaultValues: { reason: '' } });
  const isDraft = agreement.status === 'draft';

  const withdraw = useApiMutation<unknown, string>(
    {
      mutationFn: (reason) => api.delete(STAFF.agreement(agreement.id), { body: { reason } }),
      // `clients` too: the client summary's "Before opening a matter" panel points at the
      // newest open letter, and the matter's work-lock notice links to it.
      invalidates: [qk.all('agreements'), qk.all('clients'), qk.all('cases'), qk.all('leads')],
    },
    {
      onSuccess: () => {
        toast({
          title: isDraft ? 'Draft deleted' : 'Agreement withdrawn',
          description: isDraft
            ? undefined
            : 'The signing link no longer works, and the client sees it as withdrawn.',
          tone: 'success',
        });
        onClose();
        onDone?.();
      },
    },
  );

  return (
    <Modal
      open
      onClose={onClose}
      title={isDraft ? `Delete the draft “${agreement.title}”?` : `Withdraw “${agreement.title}”?`}
    >
      <Form
        form={form}
        ariaLabel={isDraft ? 'Delete this draft agreement' : 'Withdraw this agreement'}
        error={withdraw.error}
        onSubmit={async (values: Values) => {
          await withdraw.mutateAsync(values.reason.trim());
          form.reset();
        }}
      >
        {isDraft ? (
          <p className="text-sm text-ink">
            Nothing was sent, so nobody outside the firm has seen it. It disappears from every
            list. A record of it, with your name, the time and the reason below, stays.
          </p>
        ) : (
          <>
            <p className="text-sm text-ink">
              <strong className="font-semibold">This agreement has been sent.</strong> Its
              signing link will stop working, and the client will see it as withdrawn. It
              cannot be issued again — draft a new one if the terms change.
            </p>
            <p className="text-sm text-ink-muted">
              It stays on this file as withdrawn, with your name, the time and the reason
              below. Anyone who already signed stays on the record.
            </p>
          </>
        )}

        <FormTextarea
          form={form}
          name="reason"
          label={isDraft ? 'Why is this draft being deleted' : 'Why is this agreement being withdrawn'}
          rows={3}
          autoFocus
          hint="For example: drafted against the wrong client, or the terms have changed."
        />

        <FormActions
          submitLabel={isDraft ? 'Delete draft' : 'Withdraw agreement'}
          destructive
          busy={withdraw.isPending}
          onCancel={onClose}
        />
      </Form>
    </Modal>
  );
}
